import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import WelfareCardGrid from '@/pages/home/components/WelfareCardGrid';
import { welfareItems, categories, siteConfig } from '@/mocks/welfare';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  const filteredItems = useMemo(() => {
    if (activeCategory === 'all') return welfareItems;
    return welfareItems.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.id === activeCategory);
  const pageTitle = currentCategory
    ? `${currentCategory.name} - ${siteConfig.name}`
    : `全部福利 - ${siteConfig.name}`;
  const pageDesc = currentCategory
    ? `${currentCategory.description}，福利导航网为您汇集最新最全的${currentCategory.name}资源。`
    : siteConfig.description;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `${siteConfig.url}/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${siteConfig.keywords},${currentCategory?.name || '全部福利'}`}
        canonicalUrl={`${siteConfig.url}/category/${activeCategory}`}
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '全部福利' },
            ]}
          />
        </div>

        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
              {currentCategory?.name || '全部福利'}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    window.history.replaceState(null, '', `/category/${cat.id}`);
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredItems.length > 0 ? (
              <div data-product-shop>
                <WelfareCardGrid items={filteredItems} />
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-700 mb-2">暂无福利</h3>
                <p className="text-sm text-foreground-500 mb-6">该分类下暂时没有福利资源，请查看其他分类</p>
                <button
                  onClick={() => setActiveCategory('all')}
                  className="px-6 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
                >
                  查看全部福利
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3">没有找到想要的福利？</h2>
            <p className="text-sm text-foreground-500 mb-6">福利资源每日更新，收藏本站不错过任何优惠</p>
            <Link
              to="/about#contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 border border-background-200 text-foreground-800 rounded-lg text-sm font-medium hover:border-primary-300 transition-colors"
            >
              <i className="ri-mail-send-line"></i> 向我们反馈
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}